import { Button, type ButtonProps } from "@/v2-components/ui/button";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import { ProductUtil } from "@/utils/ProductUtil";
import { useProducts } from "@/services/product/useProductService";
import { useAddItemToCart } from "@/hooks/useAddItemToCart";

export default function NotifyOrAddButton({
  productSlug,
  className,
  label,
  variant = "blue-fill",
  onClick,
  children,
  onAdd,
  trigger = "unknown",
  notifyOnAdd,
  ...rest
}: ButtonProps & {
  productSlug: string;
  onAdd?: () => void;
  label?: string;
  onClick?: () => void;
  /**
   * `add_to_cart` 의 진입점 (#5292). **이 버튼이 놓인 표면이 정한다** — 제품 카드 네 곳과
   * `/cart` 추천 레일이 같은 버튼을 쓴다. 안 주면 `unknown` 이라 GA 에서 드러난다.
   */
  trigger?: CartTrigger;
  /**
   * 담기 완료 팝(#5366)을 띄울 표면인가 — **표면이 정한다**(기본 `true`).
   * 카트 화면처럼 담은 결과가 그 자리에 바로 보이는 표면만 `false` 로 끈다.
   */
  notifyOnAdd?: boolean;
}) {
  const { t } = useTrans(["tactsuit"]);
  const { addItemToCart, sheets } = useAddItemToCart(trigger, notifyOnAdd);
  const text = t(label || "Add to Bag", { ns: "gnb" });
  const { data: products } = useProducts();
  const isSoldOut = ProductUtil.isProductSoldOut(
    products?.find((p) => p.slug === productSlug),
  );

  if (isSoldOut) {
    return (
      <NotifyMe
        className={cn("px-6 text-coolgray-90", className)}
        productSlug={productSlug}
        {...rest}
      />
    );
  }

  return (
    <>
      {sheets}
      <Button
        onClick={() => {
          if (onAdd) {
            onAdd();
          } else {
            addItemToCart(productSlug);
          }
          onClick?.();
        }}
        size={"sm"}
        shape={"pill"}
        variant={variant}
        className={cn("px-6", className)}
        {...rest}
      >
        {t(text)}
        {!!children && children}
      </Button>
    </>
  );
}
